// Dono: B5 (conta e captura). /conta: conta e uso, preferências, exportar/importar, sobre/diagnóstico.
// Esqueleto medido na página de conta da referência (coluna de 700 + lateral de 300, blocos de raio 3), textos e desenho
// nossos. Preferências sincronizadas (spec §4.2) também moram aqui: os efeitos no fim do arquivo valem para o app inteiro.
import "../../styles/account.css";
import { effect } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { UsageResponse } from "../../../../shared/types.ts";
import type { PrefValues } from "../../data/index.ts";
import { logout } from "../App.tsx";
import { IconChevronDown, IconCopy, IconPrev, IconRing } from "../Icons.tsx";
import { absoluteTime, DENSITY, type Density, syncView } from "../lib.ts";
import { diagnosticText, platformOf } from "./diag.ts";
import { IconMail, IconPhone, IconPuzzle, IconRebuild, IconShareSheet } from "../share/icons.tsx";
import { importText } from "../share/inbox.ts";
import { aiEnabled, backTo, density, go, me, showModal, store, theme, toast, toasts, type Theme } from "../state.ts";
import { DeleteAccount } from "./DeleteAccount.tsx";

// ---------- Peças comuns às páginas de conta (também usadas em DevicesPage) ----------

/** Link interno sem recarregar (Ctrl/Cmd+clique abre aba nova normalmente). */
export const follow = (path: string) => (e: MouseEvent) => {
  if (e.button || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  go(path);
};

export const BackLink = () => (
  <a class="acc-back" href="/" onClick={backTo("/")}>
    <span class="acc-back-dot">
      <IconPrev />
    </span>
    <span class="acc-back-label">Voltar para o fai.g</span>
  </a>
);

export const plural = (n: number, one: string, many: string) => `${n.toLocaleString("pt-BR")} ${n === 1 ? one : many}`;

export function bytes(n: number): string {
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) (v /= 1024), i++;
  return `${v.toLocaleString("pt-BR", { maximumFractionDigits: i && v < 10 ? 1 : 0 })} ${units[i]}`;
}

/** Bolinha "?" com dica no hover e no foco (o texto também é o nome acessível). */
export const Hint = ({ text }: { text: string }) => (
  <button type="button" class="hint" aria-label={`Ajuda: ${text}`} data-tip={text}>
    ?
  </button>
);

/**
 * Confirmação em <dialog> modal: prende o foco, Esc ou clique fora cancelam, o foco começa em "Cancelar" e volta para
 * quem abriu. A ação só roda pelo botão de confirmar.
 */
export function Confirm(props: { id: string; open: boolean; title: string; action: string; children: ComponentChildren; onConfirm: () => void; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current!;
    if (props.open && !d.open) showModal(d);
    if (!props.open && d.open) d.close();
  }, [props.open]);
  return (
    <dialog
      ref={ref}
      id={props.id}
      class="acc-confirm"
      aria-labelledby={`${props.id}-title`}
      aria-describedby={`${props.id}-text`}
      onClose={props.onClose}
      onClick={(e) => e.target === e.currentTarget && e.currentTarget.close()}
    >
      <div class="acc-confirm-box">
        <h2 id={`${props.id}-title`}>{props.title}</h2>
        <div id={`${props.id}-text`} class="acc-confirm-text">
          {props.children}
        </div>
        <div class="acc-confirm-actions">
          <button type="button" class="acc-btn" autofocus onClick={() => ref.current?.close()}>
            Cancelar
          </button>
          <button
            type="button"
            class="acc-btn primary"
            onClick={() => {
              ref.current?.close();
              props.onConfirm();
            }}
          >
            {props.action}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function Seg<T extends string>({ labelledby, value, options, onPick }: { labelledby: string; value: T; options: [T, string][]; onPick: (v: T) => void }) {
  return (
    <div class="acc-seg" role="group" aria-labelledby={labelledby}>
      {options.map(([v, text]) => (
        <button type="button" key={v} aria-pressed={value === v} onClick={() => onPick(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

const Switch = ({ id, on, onFlip }: { id: string; on: boolean; onFlip: (on: boolean) => void }) => (
  <button type="button" role="switch" id={id} class="acc-switch" aria-checked={on} aria-labelledby={`${id}-label`} onClick={() => onFlip(!on)} />
);

// ---------- Página ----------

const THEMES: [Theme, string][] = [
  ["light", "Claro"],
  ["dark", "Escuro"],
  ["system", "Sistema"],
];
const FONTS: [PrefValues["noteFont"], string][] = [
  ["sans", "Sans (Nunito)"],
  ["serif", "Serifada"],
  ["mono", "Monoespaçada"],
];
const setPrefs = (v: Partial<PrefValues>) => store.setPrefs(v).catch(() => toast({ key: "prefs", text: "Não deu para salvar a preferência. Tente de novo." }));

type Counts = { cards: number; trash: number; tags: number; spaces: number };
const describe = (c: Counts) =>
  `${plural(c.cards, "card", "cards")}, ${c.trash.toLocaleString("pt-BR")} na lixeira, ${plural(c.tags, "etiqueta", "etiquetas")}, ${plural(c.spaces, "coleção", "coleções")}`;
const localCounts = (): Counts => ({ cards: store.cards.peek().length, trash: store.trash.peek().length, tags: store.tags.peek().size, spaces: store.spaces.peek().length });

export function AccountPage() {
  const m = me.value!;
  const name = m.email.split("@")[0];
  const online = store.status.value.online;
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  useEffect(() => scrollTo(0, 0), []);
  // aiEnabled também decide, no detalhe, se o card guardado sem a chave oferece "Analisar de novo" (state.ts).
  useEffect(() => void (online && store.usage().then((u) => (setUsage(u), (aiEnabled.value = u.aiEnabled)), () => {})), [online]);
  const cards = usage?.cards ?? store.cards.value.length;
  return (
    <main id="main" tabIndex={-1} class="page acc">
      <BackLink />
      <div class="acc-wrap">
        <h1 class="acc-title">
          Bom ver você por aqui, <span class="acc-name">{name}</span>
        </h1>
        <div class="acc-cols">
          <div class="acc-main">
            <section class="acc-block" aria-labelledby="acc-usage">
              <div class="acc-head">
                <h2 id="acc-usage">Seu acervo</h2>
                <span class="acc-plan">
                  <IconRing />
                  Uso pessoal
                </span>
              </div>
              <p class="acc-lead">
                Você guardou <u>{cards.toLocaleString("pt-BR")}</u> {cards === 1 ? "card" : "cards"}. Não há limite de quantidade.
              </p>
              <Usage usage={usage} cards={cards} />
              <Transfer online={online} />
            </section>
            <section class="acc-block acc-split" aria-labelledby="acc-tags">
              <h2 id="acc-tags">Suas etiquetas</h2>
              <a href="/etiquetas" onClick={follow("/etiquetas")}>
                Gerenciar etiquetas
              </a>
            </section>
            <section class="acc-block acc-split" aria-labelledby="acc-devices">
              <h2 id="acc-devices">
                Aparelhos e extensões <span class="acc-badge">beta</span>
              </h2>
              <a href="/aparelhos" onClick={follow("/aparelhos")}>
                Parear e revogar
              </a>
            </section>
            <section class="acc-block acc-split" aria-labelledby="acc-api">
              <h2 id="acc-api">
                API e agentes <span class="acc-badge">beta</span>
              </h2>
              <a href="/api-e-agentes" onClick={follow("/api-e-agentes")}>
                Chaves e MCP
              </a>
            </section>
            <section class="acc-block" aria-labelledby="acc-mail">
              <div class="acc-head">
                <h2 id="acc-mail">Seu e-mail</h2>
              </div>
              <p class="acc-mailrow">
                <IconMail />
                <span class="acc-address">{m.email}</span>
                <span class="acc-tagline">principal</span>
              </p>
            </section>
            <section class="acc-block acc-split acc-thin" aria-label="Entrada">
              <span>Entrada com Google</span>
              <span class="acc-caps">Conta Google ligada</span>
            </section>
            <Prefs />
            <About />
          </div>
          <Aside />
        </div>
        <div class="acc-actions">
          <DeleteAccount />
          <button type="button" class="acc-arrow" onClick={() => logout(m.id)}>
            → Sair do fai.g
          </button>
          <button type="button" class="acc-arrow" onClick={() => document.getElementById("acc-export")?.click()}>
            → Exportar meu acervo
          </button>
        </div>
      </div>
    </main>
  );
}

function Usage({ usage, cards }: { usage: UsageResponse | null; cards: number }) {
  const ai = usage ? `${usage.aiCallsToday.toLocaleString("pt-BR")} de ${usage.aiDailyLimit.toLocaleString("pt-BR")}` : "—";
  return (
    <div class="acc-art">
      <dl class="acc-stats">
        <div>
          <dt>{cards === 1 ? "card guardado" : "cards guardados"}</dt>
          <dd>{cards.toLocaleString("pt-BR")}</dd>
        </div>
        <div>
          <dt>em arquivos</dt>
          <dd>{usage ? bytes(usage.bytes) : "—"}</dd>
        </div>
        {usage?.aiEnabled === false ? (
          // Sem a chave da IA (a produção hoje): nada de "0 de 100"; o resumo e as etiquetas automáticas vêm depois.
          <div class="acc-soon">
            <dt>análise automática</dt>
            <dd>em breve</dd>
          </div>
        ) : (
          <div>
            <dt>análises de IA hoje</dt>
            <dd>{ai}</dd>
            {usage && <progress max={Math.max(1, usage.aiDailyLimit)} value={usage.aiCallsToday} aria-label="Análises de IA usadas hoje" />}
          </div>
        )}
      </dl>
      {!usage && <p class="acc-art-note">Os números do servidor aparecem com conexão.</p>}
    </div>
  );
}

function Transfer({ online }: { online: boolean }) {
  const pick = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState("");
  const hasFiles = store.cards.value.some((c) => c.primaryBlobId || c.coverBlobId);
  const exportAll = () => {
    setExporting(true);
    // Arquivo que não baixa fica fora do zip e vira aviso do store (API.md, "Avisos"): aí não está "pronta", é parcial.
    // ponytail: qualquer aviso novo durante a exportação de uma conta com arquivos conta como parcial; exato quando
    // downloadExport devolver quantos ficaram de fora.
    const before = new Set(toasts.peek().map((t) => t.key));
    store
      .downloadExport()
      .then(
        () =>
          hasFiles && toasts.peek().some((t) => !before.has(t.key))
            ? toast({ key: "export-partial", text: "O .zip foi baixado sem alguns arquivos. Com conexão, exporte de novo para ter tudo." }, 10000)
            : toast({ key: "export", text: "Exportação pronta: o .zip está nos seus downloads." }),
        () => toast({ key: "export-fail", text: "Não deu para exportar agora. Tente de novo." }),
      )
      .finally(() => setExporting(false));
  };
  const importAll = async (files: File[]) => {
    if (!files.length) return;
    setImporting(true);
    setReport("");
    try {
      setReport(importText(await store.importFiles(files)));
    } catch {
      setReport("A importação não terminou. O aviso diz o motivo.");
    } finally {
      setImporting(false);
    }
  };
  return (
    <div class="acc-sub">
      <h3>Exportar e importar</h3>
      <p>Baixe tudo num .zip, com cards, etiquetas, coleções e arquivos. Para trazer de volta, use um .zip do fai.g ou notas em .md e .txt.</p>
      <div class="acc-buttons">
        <button type="button" id="acc-export" class="acc-btn primary" aria-busy={exporting} disabled={exporting} onClick={exportAll}>
          {exporting ? "Preparando o .zip…" : "Exportar .zip"}
        </button>
        <button type="button" class="acc-btn" aria-busy={importing} disabled={importing || !online} onClick={() => pick.current?.click()}>
          {importing ? "Importando…" : "Importar arquivos"}
        </button>
        <input
          ref={pick}
          type="file"
          accept=".zip,.md,.markdown,.txt"
          multiple
          hidden
          aria-label="Arquivos para importar"
          onChange={(e) => {
            const files = [...(e.currentTarget.files ?? [])];
            e.currentTarget.value = "";
            void importAll(files);
          }}
        />
      </div>
      {!online && <p class="acc-note">{hasFiles ? "Sem conexão: importar fica para depois, e o .zip sai sem os arquivos que este aparelho ainda não abriu." : "Importar precisa de conexão."}</p>}
      <p class="acc-note" role="status">
        {report}
      </p>
    </div>
  );
}

function Prefs() {
  const p = store.prefs.value;
  const [count, setCount] = useState(String(p.serendipityCount));
  useEffect(() => setCount(String(p.serendipityCount)), [p.serendipityCount]);
  const commitCount = () => {
    const n = Number(count);
    if (Number.isInteger(n) && n >= 1 && n <= 100) void setPrefs({ serendipityCount: n });
    else setCount(String(p.serendipityCount));
  };
  return (
    <section class="acc-block" aria-labelledby="acc-prefs">
      <div class="acc-head">
        <h2 id="acc-prefs">Preferências</h2>
      </div>
      <div class="acc-row">
        <span id="pref-theme" class="acc-label">
          Tema
        </span>
        <Seg labelledby="pref-theme" value={theme.value} options={THEMES} onPick={(t) => (theme.value = t)} />
      </div>
      <div class="acc-row">
        <span id="pref-density" class="acc-label">
          Densidade da grade
        </span>
        <Seg labelledby="pref-density" value={density.value} options={(Object.keys(DENSITY) as Density[]).map((d) => [d, DENSITY[d].label])} onPick={(d) => (density.value = d)} />
      </div>
      <div class="acc-row">
        <label for="pref-font" class="acc-label">
          Fonte das notas
        </label>
        <span class="acc-select">
          <select id="pref-font" value={p.noteFont} onChange={(e) => void setPrefs({ noteFont: e.currentTarget.value as PrefValues["noteFont"] })}>
            {FONTS.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
          <IconChevronDown />
        </span>
      </div>
      <div class="acc-row">
        <span id="pref-captions-label" class="acc-label">
          Legendas sob os cards
        </span>
        <Hint text="Título ou domínio embaixo de imagens e links." />
        <Switch id="pref-captions" on={p.captions} onFlip={(on) => void setPrefs({ captions: on })} />
      </div>
      <div class="acc-row">
        <span id="pref-minimal-label" class="acc-label">
          Modo mínimo
        </span>
        <Hint text="Tira legendas e domínios da grade: fica só o que você guardou." />
        <Switch id="pref-minimal" on={p.minimalist} onFlip={(on) => void setPrefs({ minimalist: on })} />
      </div>
      <div class="acc-row">
        <label for="pref-count" class="acc-label">
          Cards por rodada do Reencontro
        </label>
        <input
          id="pref-count"
          class="acc-number"
          type="number"
          inputMode="numeric"
          min={1}
          max={100}
          step={1}
          value={count}
          onInput={(e) => setCount(e.currentTarget.value)}
          onChange={commitCount}
        />
      </div>
      <p class="acc-note">Valem em todos os seus aparelhos.</p>
    </section>
  );
}

type Diag = { shell: string | null; worker: boolean; used: number | null; persisted: boolean };
async function readDiag(): Promise<Diag> {
  const keys = typeof caches === "undefined" ? [] : await caches.keys().catch(() => [] as string[]);
  const est = await navigator.storage?.estimate?.().catch(() => null);
  return {
    shell: keys.find((k) => k.startsWith("shell-"))?.slice(6) ?? null,
    worker: !!navigator.serviceWorker?.controller,
    used: est?.usage ?? null,
    persisted: (await navigator.storage?.persisted?.().catch(() => false)) ?? false,
  };
}

function About() {
  const s = store.status.value;
  const [, label] = syncView(s);
  const [diag, setDiag] = useState<Diag | null>(null);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  useEffect(() => void readDiag().then(setDiag), [busy]);
  const counts: Counts = { cards: store.cards.value.length, trash: store.trash.value.length, tags: store.tags.value.size, spaces: store.spaces.value.length };
  const { browser, os } = platformOf(navigator.userAgent);
  const facts: [string, string][] = [
    ["Versão do app", diag ? (diag.shell ?? "sem cópia offline (desenvolvimento)") : "…"],
    ["Funciona sem conexão", diag ? (diag.worker ? "sim" : "não") : "…"],
    ["Navegador", browser],
    ["Sistema", os],
    ["Sincronização", label],
    ["Última sincronização", s.lastSyncAt ? absoluteTime(s.lastSyncAt) : "ainda não"],
    ["Checkpoint", store.checkpoint().toLocaleString("pt-BR")],
    ["Alterações na fila", s.pending.toLocaleString("pt-BR")],
    ["Último erro", s.error ?? "nenhum"],
    ["Cópia local", describe(counts)],
    ["Espaço no navegador", diag?.used == null ? "…" : `${bytes(diag.used)}${diag.persisted ? ", protegido" : ""}`],
  ];
  const copy = () =>
    navigator.clipboard
      .writeText(diagnosticText(facts))
      .then(
        () => toast({ key: "diag-copied", text: "Diagnóstico copiado. Cole na sua mensagem de suporte." }),
        () => toast({ key: "diag-copy-fail", text: "Não deu para copiar. Selecione o texto da lista e copie." }),
      );
  const blocked = !s.online ? "Precisa de conexão." : s.pending ? "Espere a fila de envio esvaziar." : "";
  const rebuild = async () => {
    const before = localCounts();
    setBusy(true);
    setResult("");
    const after = await store.rebuildLocalCopy().catch(() => null);
    setBusy(false);
    setResult(after ? `Cópia refeita: ${describe(after)}. Antes: ${describe(before)}.` : "A cópia local não foi refeita. Nada foi apagado.");
  };
  return (
    <section id="sobre" tabIndex={-1} class="acc-block" aria-labelledby="acc-about">
      <div class="acc-head">
        <h2 id="acc-about">Sobre este aparelho</h2>
      </div>
      <p class="acc-local">
        Este aparelho guarda uma cópia do seu acervo para funcionar sem conexão.{" "}
        {diag === null ? "" : diag.persisted ? (
          <strong>O navegador protege essa cópia: ela só sai quando você sai da conta ou a reconstrói.</strong>
        ) : (
          "O navegador pode apagá-la se faltar espaço; o que já foi enviado continua na sua conta."
        )}
      </p>
      <dl class="acc-facts">
        {facts.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <div class="acc-buttons">
        <button type="button" class="acc-btn" onClick={copy}>
          <IconCopy />
          Copiar diagnóstico
        </button>
      </div>
      <div class="acc-sub">
        <h3>Reconstruir cópia local</h3>
        <p>Apaga o que este navegador guardou e baixa tudo de novo do servidor. Nada se perde na sua conta.</p>
        <div class="acc-buttons">
          <button type="button" class="acc-btn" disabled={busy || !!blocked} aria-busy={busy} onClick={() => setAsking(true)}>
            <IconRebuild />
            {busy ? "Reconstruindo…" : "Reconstruir cópia local"}
          </button>
        </div>
        {blocked && <p class="acc-note">{blocked}</p>}
        <p class="acc-note" role="status">
          {result}
        </p>
      </div>
      <Confirm id="rebuild" open={asking} title="Reconstruir a cópia local?" action="Reconstruir" onClose={() => setAsking(false)} onConfirm={() => void rebuild()}>
        <p>Os dados deste navegador serão apagados e baixados de novo. Leva alguns segundos; a sua conta não muda.</p>
      </Confirm>
    </section>
  );
}

const goGuide = (g: string) => follow(`/aparelhos?guia=${g}`);

function Aside() {
  return (
    <aside class="acc-aside" aria-label="Guardar de outros lugares">
      <section class="acc-block acc-promo">
        <span class="acc-ribbon">essencial</span>
        <p>
          <a href="/aparelhos?guia=extensao" onClick={goGuide("extensao")}>
            Instale a extensão
          </a>{" "}
          e guarde páginas, imagens e trechos sem sair delas.
        </p>
        <ul class="acc-ways">
          <li>
            <a href="/aparelhos?guia=extensao" onClick={goGuide("extensao")} aria-label="Extensão do navegador" data-tip="Extensão">
              <IconPuzzle />
            </a>
          </li>
          <li>
            <a href="/aparelhos?guia=android" onClick={goGuide("android")} aria-label="Android" data-tip="Android">
              <IconPhone />
            </a>
          </li>
          <li>
            <a href="/aparelhos?guia=iphone" onClick={goGuide("iphone")} aria-label="iPhone" data-tip="iPhone">
              <IconShareSheet />
            </a>
          </li>
        </ul>
      </section>
      <section class="acc-block acc-mobile" aria-labelledby="acc-pocket">
        <h2 id="acc-pocket">
          Seu acervo
          <br />
          no bolso.
        </h2>
        <p>Instale o fai.g na tela inicial e guarde direto da folha Compartilhar.</p>
        <div class="acc-phone" aria-hidden="true">
          <span class="acc-phone-bar" />
          <span />
          <span />
          <span />
          <span />
        </div>
        <div class="acc-stores">
          <a href="/aparelhos?guia=android" onClick={goGuide("android")}>
            Android
          </a>
          <a href="/aparelhos?guia=iphone" onClick={goGuide("iphone")}>
            iPhone
          </a>
        </div>
      </section>
      <section class="acc-block acc-help">
        <p>Algo estranho na sincronização?</p>
        <button
          type="button"
          class="acc-outline"
          onClick={() => {
            const about = document.getElementById("sobre");
            about?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
            about?.focus({ preventScroll: true });
          }}
        >
          Ver diagnóstico
        </button>
      </section>
    </aside>
  );
}

// ---------- Preferências sincronizadas → este aparelho, e este aparelho → preferências ----------
// Tema e densidade continuam também no localStorage (pintura sem piscar). Vindo do sync, só uma MUDANÇA de valor mexe
// aqui: o padrão de antes do primeiro pull não apaga a escolha local. Mudar no trilho, no menu ou na Conta muda o
// signal, e o segundo efeito manda a preferência para a conta.
let seen: PrefValues | null = null;
effect(() => {
  const p = store.prefs.value;
  const root = document.documentElement;
  root.dataset.noteFont = p.noteFont;
  root.toggleAttribute("data-no-captions", !p.captions);
  root.toggleAttribute("data-minimalist", p.minimalist);
  const before = seen;
  seen = p;
  if (!before) return;
  if (p.theme !== before.theme) theme.value = p.theme;
  if (p.density !== before.density) density.value = p.density;
});
let first = true;
effect(() => {
  const t = theme.value;
  const d = density.value;
  if (first) return void (first = false);
  const p = store.prefs.peek();
  const diff: Partial<PrefValues> = {};
  if (t !== p.theme) diff.theme = t;
  if (d !== p.density) diff.density = d;
  if (Object.keys(diff).length) store.setPrefs(diff).catch(() => {}); // sem conta aberta: fica só neste aparelho
});
